import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Form, Modal, Segmented, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import type { RootState } from '@/redux';
import { posService } from '@/services/pos.service';
import { getApiError } from '@/utils/apiError';
import { getPosShiftStoreId, usePosShiftStoreId } from '@/utils/posShift';
import { CustomerSidebar } from './components/CustomerSidebar';
import type { CustomerSidebarHandle } from './components/CustomerSidebar';
import { QRSearchBar } from './components/QRSearchBar';
import { BulkQrModalButton } from './components/BulkQrModalButton';
import { OrderItemsTable } from './components/OrderItemsTable';
import { recalcItem, applySwappedVariant, computePosTotals } from './utils/orderItemCalc';
import { mapDetailToPosVariant } from './utils/mapVariantDetail';
import { validatePosItems, type PosItemError } from './utils/validatePosItems';
import { PaymentFooter } from './components/PaymentFooter';
import { NfcScanOverlay } from './components/NfcScanOverlay';
import { CheckoutSuccessDialog } from './components/CheckoutSuccessDialog';
import type { CheckoutSuccessResult } from './utils/buildReceiptData';
import { GoldPriceStaleBanner } from './components/GoldPriceStaleBanner';
import { useStaleGoldItems } from './hooks/useStaleGoldItems';
import { useRefreshStalePrices } from './hooks/useRefreshStalePrices';
import { useStalePricePreview } from './hooks/useStalePricePreview';
import { usePosTabs, MAX_TABS } from './hooks/usePosTabs';
import { usePosTabsHotkeys } from './hooks/usePosTabsHotkeys';
import { PosTabsBar } from './components/PosTabsBar';
import { usePosFullscreen } from './PosFullscreenContext';
import { UserType } from './types';
import type {
  OrderItem,
  OrderType,
  FulfillmentMode,
  PaymentMethod,
  PosVariant,
  CommissionStaff,
} from './types';

function variantToOrderItem(variant: PosVariant, fulfillmentMode: FulfillmentMode): OrderItem {
  const price = variant.price;
  const quantity = 1;
  return {
    id: uuidv4(),
    variantId: variant.variant_id,
    variantName: variant.variant_name,
    variantCode: variant.variant_code,
    specificationCode: variant.specification_code,
    trackingMode: variant.tracking_mode,
    pricingMethod: variant.pricing_method,
    price,
    discountPercent: 0,
    discountAmount: 0,
    quantity,
    weight: variant.weight,
    laborFee: variant.labor_fee,
    vat: variant.vat,
    totalAmount:
      variant.tracking_mode === 'quantity' || fulfillmentMode === 'PREORDER'
        ? price * quantity
        : price,
    warehouseStaffIds: variant.warehouse_staff_ids,
    instrumentCode: variant.instrument_code,
    appliedBid: variant.applied_bid,
    appliedAsk: variant.applied_ask,
    appliedTs: variant.applied_ts,
    appliedOverride: variant.override_applied,
  };
}

export function POSPage() {
  const [form] = Form.useForm();
  // antd Form is uncontrolled — typing in inputs doesn't re-render the parent.
  // Force re-render on every value change so isCheckoutReady re-evaluates live.
  const [, bumpFormTick] = useReducer((n: number) => n + 1, 0);
  const sidebarRef = useRef<CustomerSidebarHandle | null>(null);

  const { state: tabsState, active, addTab, closeTab, switchTab, patchActive } = usePosTabs();
  const { posAppFullscreen } = usePosFullscreen();
  const [tabsPortalEl, setTabsPortalEl] = useState<HTMLElement | null>(null);

  // Tìm portal slot trong Header khi vào fullscreen; clear khi thoát.
  useEffect(() => {
    setTabsPortalEl(posAppFullscreen ? document.getElementById('pos-tabs-portal-slot') : null);
  }, [posAppFullscreen]);

  const activeRef = useRef(active);
  useEffect(() => {
    activeRef.current = active;
  });

  const orderItems = active.orderItems;
  const orderSubtotal = orderItems.reduce((sum, i) => sum + i.totalAmount, 0);
  const orderType = active.orderType;
  const fulfillmentMode = active.fulfillmentMode;
  const paymentMethod = active.paymentMethod;
  const selectedSessionId = active.selectedSessionId;
  const commissionStaff = active.commissionStaff;
  const cashReceived = active.cashReceived;
  const paymentAccountId = active.paymentAccountId;
  const nonCashAmount = active.nonCashAmount;

  const setOrderItems = useCallback(
    (updater: OrderItem[] | ((prev: OrderItem[]) => OrderItem[])) => {
      const next = typeof updater === 'function' ? updater(activeRef.current.orderItems) : updater;
      patchActive({ orderItems: next });
    },
    [patchActive],
  );
  const setOrderType = useCallback((v: OrderType) => patchActive({ orderType: v }), [patchActive]);
  const setPaymentMethod = useCallback(
    // Reset account + split khi đổi method để NonCashPaymentSection auto-chọn
    // default của method mới (tránh gửi nhầm account id của method cũ).
    (v: PaymentMethod) =>
      patchActive({ paymentMethod: v, paymentAccountId: undefined, nonCashAmount: undefined }),
    [patchActive],
  );
  const setSelectedSessionId = useCallback(
    (v: string | undefined) => patchActive({ selectedSessionId: v }),
    [patchActive],
  );
  const setCommissionStaff = useCallback(
    (v: CommissionStaff[]) => patchActive({ commissionStaff: v }),
    [patchActive],
  );
  const setCashReceived = useCallback(
    (v: number) => patchActive({ cashReceived: v }),
    [patchActive],
  );
  const setPaymentAccountId = useCallback(
    (v: string | undefined) => patchActive({ paymentAccountId: v }),
    [patchActive],
  );
  const setNonCashAmount = useCallback(
    (v: number | undefined) => patchActive({ nonCashAmount: v }),
    [patchActive],
  );

  const shiftStoreId = usePosShiftStoreId();
  const selectedStoreId = shiftStoreId ?? undefined;
  const [successOpen, setSuccessOpen] = useState(false);
  const [successResult, setSuccessResult] = useState<CheckoutSuccessResult | null>(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [itemErrors, setItemErrors] = useState<Record<string, PosItemError>>({});

  const prevActiveIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (prevActiveIdRef.current === active.id) return;
    form.resetFields();
    if (Object.keys(active.customerForm).length > 0) {
      form.setFieldsValue(active.customerForm);
    }
    prevActiveIdRef.current = active.id;
  }, [active.id, active.customerForm, form]);

  const handleSwitchTab = useCallback(
    (nextId: string) => {
      if (nextId === active.id) return;
      patchActive({ customerForm: form.getFieldsValue() });
      switchTab(nextId);
    },
    [active.id, form, patchActive, switchTab],
  );

  const handleCloseTabRequest = useCallback(
    (id: string) => {
      const tab = tabsState.tabs.find((t) => t.id === id);
      if (!tab) return;
      const hasData =
        tab.orderItems.length > 0 ||
        Object.values(tab.customerForm).some((v) => v !== undefined && v !== '' && v !== null);
      if (!hasData) {
        closeTab(id);
        return;
      }
      Modal.confirm({
        title: 'Đóng đơn này?',
        content: 'Dữ liệu trong đơn sẽ bị mất.',
        okText: 'Đóng',
        cancelText: 'Huỷ',
        onOk: () => closeTab(id),
      });
    },
    [tabsState.tabs, closeTab],
  );

  useEffect(() => {
    const flushOnHidden = () => {
      // Only flush khi tab/cửa sổ đang chuyển sang hidden; lúc visible lại sẽ
      // overwrite snapshot bằng giá trị có thể stale (vd nếu user vừa switch tab POS).
      if (document.visibilityState === 'hidden') {
        patchActive({ customerForm: form.getFieldsValue() });
      }
    };
    const flushOnUnload = () => {
      patchActive({ customerForm: form.getFieldsValue() });
    };
    document.addEventListener('visibilitychange', flushOnHidden);
    window.addEventListener('beforeunload', flushOnUnload);
    return () => {
      document.removeEventListener('visibilitychange', flushOnHidden);
      window.removeEventListener('beforeunload', flushOnUnload);
    };
  }, [form, patchActive]);

  const hotkeyOnClose = useCallback(
    () => handleCloseTabRequest(active.id),
    [handleCloseTabRequest, active.id],
  );
  const hotkeyOnSwitchByIndex = useCallback(
    (idx: number) => {
      const target =
        idx === -1 ? tabsState.tabs[tabsState.tabs.length - 1] : tabsState.tabs[idx];
      if (target) handleSwitchTab(target.id);
    },
    [tabsState.tabs, handleSwitchTab],
  );

  usePosTabsHotkeys({
    onNew: addTab,
    onClose: hotkeyOnClose,
    onSwitchByIndex: hotkeyOnSwitchByIndex,
  });

  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.auth.user);

  // EG-369: enforce shift presence. Auto-init at login covers cashier + 1-store users;
  // multi-store and zero-store cases land here.
  useEffect(() => {
    if (getPosShiftStoreId()) return; // shift opened or auto-init succeeded
    const assignments = user?.storeAssignments ?? [];
    if (assignments.length === 0) {
      void message.error('Tài khoản chưa được gán cửa hàng nào. Liên hệ admin.');
      navigate('/');
      return;
    }
    // Multi-store with no shift → must pick via Shifts page
    navigate('/pos/shifts');
  }, [navigate, user]);

  const addItem = useCallback(
    (variant: PosVariant) => {
      const newItem = variantToOrderItem(variant, fulfillmentMode);

      setOrderItems((prev) => {
        const isSerial = variant.tracking_mode === 'serial';
        const isInstant = fulfillmentMode === 'INSTANT';

        if (isSerial && isInstant) {
          if (prev.find((i) => i.specificationCode === variant.specification_code)) {
            void message.warning(
              `Serial ${variant.specification_code ?? variant.variant_code} đã có trong đơn`,
            );
            return prev;
          }
          return [...prev, recalcItem(newItem, fulfillmentMode)];
        }

        const existing = prev.find((i) => i.variantId === variant.variant_id);
        if (existing) {
          return prev.map((i) =>
            i.variantId === variant.variant_id
              ? recalcItem({ ...i, quantity: i.quantity + 1 }, fulfillmentMode)
              : i,
          );
        }
        return [...prev, recalcItem(newItem, fulfillmentMode)];
      });

      void message.success(`Đã thêm: ${variant.variant_name}`, 1);
    },
    [fulfillmentMode, setOrderItems],
  );

  const updateItem = useCallback((id: string, patch: Partial<OrderItem>) => {
    setOrderItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, ...patch } : item)),
    );
    if (patch.sellerStaffId !== undefined || patch.warehouseStaffId !== undefined) {
      setItemErrors((prev) => {
        const cur = prev[id];
        if (!cur) return prev;
        const next: PosItemError = { ...cur };
        if (patch.sellerStaffId) delete next.seller;
        if (patch.warehouseStaffId) delete next.warehouse;
        const rest = { ...prev };
        if (!next.seller && !next.warehouse) delete rest[id];
        else rest[id] = next;
        return rest;
      });
    }
  }, [setOrderItems]);

  const goldPriceLastUpdated = useSelector((s: RootState) => s.goldPrice.lastUpdated);
  const [staleDismissedAt, setStaleDismissedAt] = useState<string | null>(null);
  const { staleItemIds, count: staleCount } = useStaleGoldItems(orderItems);
  const stalePricePreview = useStalePricePreview({
    orderItems,
    staleItemIds,
    fulfillmentMode,
  });
  const { loading: refreshingPrices, refresh: refreshPrices } = useRefreshStalePrices({
    orderItems,
    staleItemIds,
    updateItem,
    fulfillmentMode,
    preview: stalePricePreview,
  });
  const showStaleBanner = staleCount > 0 && staleDismissedAt !== goldPriceLastUpdated;

  const removeItem = useCallback((id: string) => {
    setOrderItems((prev) => prev.filter((i) => i.id !== id));
  }, [setOrderItems]);

  const [swapping, setSwapping] = useState(false);
  const swapItemSerial = useCallback(
    async (itemId: string, serial: string): Promise<{ ok: boolean; error?: string }> => {
      const item = orderItems.find((i) => i.id === itemId);
      if (!item) return { ok: false, error: 'Không tìm thấy dòng hàng' };
      // Serial là unique theo tenant → chặn nếu serial này đã nằm ở dòng khác trong đơn.
      if (orderItems.some((i) => i.id !== itemId && i.specificationCode === serial)) {
        return { ok: false, error: `Serial ${serial} đã có trong đơn` };
      }
      setSwapping(true);
      try {
        const qr = `${item.variantCode}_${serial}`;
        const { items, item_errors } = await posService.lookupVariants([qr], fulfillmentMode);
        if (item_errors.length > 0) return { ok: false, error: item_errors[0].error };
        if (items.length === 0) return { ok: false, error: 'Không tìm thấy serial' };
        const variant = mapDetailToPosVariant(items[0]);
        setOrderItems((prev) =>
          prev.map((i) => (i.id === itemId ? applySwappedVariant(i, variant, fulfillmentMode) : i)),
        );
        return { ok: true };
      } catch (err) {
        return { ok: false, error: getApiError(err)?.message ?? 'Lỗi khi đổi mã quy cách' };
      } finally {
        setSwapping(false);
      }
    },
    [orderItems, fulfillmentMode, setOrderItems],
  );

  const handleFulfillmentChange = (mode: FulfillmentMode) => {
    if (orderItems.length > 0) {
      Modal.confirm({
        title: 'Chuyển chế độ?',
        content: 'Đổi Giao ngay / Đặt trước sẽ xóa toàn bộ sản phẩm trong đơn. Tiếp tục?',
        okText: 'Xác nhận',
        cancelText: 'Huỷ',
        onOk: () =>
          patchActive({
            orderItems: [],
            paymentMethod: 'bank',
            paymentAccountId: undefined,
            nonCashAmount: undefined,
            fulfillmentMode: mode,
          }),
      });
    } else {
      patchActive({ fulfillmentMode: mode });
    }
  };

  const handleSessionChange = (sessionId: string, staff: CommissionStaff[]) => {
    setSelectedSessionId(sessionId);
    setCommissionStaff(staff);
  };

  const isCheckoutReady = (() => {
    const v = form.getFieldsValue() as {
      customer_phone?: string;
      customer_name?: string;
      customer_city_id?: string;
      citizen_id?: string;
      invoice_name?: string;
      invoice_tax_id?: string;
      invoice_address?: string;
      buyer_cccd?: string;
    };
    const hasCustomer =
      !!v.customer_phone &&
      !!v.customer_name &&
      !!v.citizen_id &&
      !!v.customer_city_id;
    const hasInvoice =
      !!v.invoice_name && !!v.invoice_tax_id && !!v.invoice_address && !!v.buyer_cccd;
    const hasItems = orderItems.length > 0;
    return hasCustomer && hasInvoice && hasItems;
  })();

  const handleCheckout = async () => {
    const errs = validatePosItems(orderItems, fulfillmentMode);
    setItemErrors(errs);
    const hasItemErrors = Object.keys(errs).length > 0;

    let formValid = true;
    let firstFormErrorName: (string | number)[] | undefined;
    try {
      await form.validateFields();
    } catch (err: unknown) {
      formValid = false;
      const errorFields = (err as { errorFields?: { name: (string | number)[] }[] })?.errorFields;
      firstFormErrorName = errorFields?.[0]?.name;
    }

    if (!formValid || hasItemErrors) {
      if (!formValid) {
        sidebarRef.current?.expandAll();
        if (firstFormErrorName) {
          setTimeout(
            () => form.scrollToField(firstFormErrorName!, { behavior: 'smooth', block: 'center' }),
            50,
          );
        }
      } else if (hasItemErrors) {
        const firstId = Object.keys(errs)[0];
        setTimeout(
          () =>
            document
              .getElementById(`pos-item-row-${firstId}`)
              ?.scrollIntoView({ behavior: 'smooth', block: 'center' }),
          50,
        );
      }
      void message.error('Vui lòng điền đầy đủ thông tin bắt buộc');
      return;
    }
    await handleCheckoutConfirm();
  };

  const handleCheckoutConfirm = async () => {
    setCheckoutLoading(true);
    try {
      const values = form.getFieldsValue() as Record<string, unknown>;

      // Strip FE-only label cache fields and the same-as-delivery checkbox.
      delete values._cityName;
      delete values._wardName;
      delete values.invoice_same_as_delivery;

      // Birthday: convert DD/MM/YYYY (from DateMaskInput) → YYYY-MM-DD (BE ISO).
      // Tolerate dayjs in case input source changes back later.
      const birthdayRaw = values.customer_birthday;
      const customerBirthday: string | undefined = (() => {
        if (!birthdayRaw) return undefined;
        if (typeof birthdayRaw === 'string') {
          const m = birthdayRaw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
          return m ? `${m[3]}-${m[2]}-${m[1]}` : undefined;
        }
        if (typeof birthdayRaw === 'object' && 'format' in (birthdayRaw as object)) {
          return (birthdayRaw as { format(fmt: string): string }).format('YYYY-MM-DD');
        }
        return undefined;
      })();

      const orderTotalForPayment = computePosTotals(orderItems).grandTotal;
      const created = await posService.createOrder({
        customer_name: values.customer_name as string,
        customer_phone: values.customer_phone as string,
        customer_address: values.customer_address as string | undefined,
        customer_city_id: values.customer_city_id as string,
        customer_ward_id: values.customer_ward_id as string | undefined,
        customer_email: values.customer_email as string | undefined,
        customer_birthday: customerBirthday,
        description: values.description as string | undefined,
        citizen_id: values.citizen_id as string,
        care_staff_id: values.care_staff_id as string | undefined,
        delivery_days: values.delivery_days as number | undefined,
        user_type: (values.user_type as UserType) ?? UserType.PERSONAL,

        invoice_name: values.invoice_name as string,
        invoice_tax_id: values.invoice_tax_id as string,
        invoice_address: values.invoice_address as string,
        invoice_email: values.invoice_email as string | undefined,
        buyer_name: values.buyer_name as string | undefined,
        buyer_cccd: values.buyer_cccd as string,
        misa_account_object_id: values.misa_account_object_id as string | undefined,

        items: orderItems.map((item) => ({
          // BE tách `variant_code_serial` theo '_' → parts[0]=variant_code, parts[1]=serial.
          // Hàng serial phải gửi `<variantCode>_<serial>`, KHÔNG gửi serial trần.
          variant_code_serial: item.specificationCode
            ? `${item.variantCode}_${item.specificationCode}`
            : item.variantCode,
          quantity: item.quantity,
          price: item.price,
          applied_price: item.price,
          discount_percent: item.discountPercent,
          seller_staff_id: item.sellerStaffId,
          warehouse_staff_id: item.warehouseStaffId,
        })),

        payment_method: paymentMethod,
        payment_account_id: paymentMethod === 'cash' ? undefined : paymentAccountId,
        non_cash_amount:
          paymentMethod === 'cash' ? 0 : (nonCashAmount ?? orderTotalForPayment),
        cash_amount:
          paymentMethod === 'cash'
            ? orderTotalForPayment
            : Math.max(0, orderTotalForPayment - (nonCashAmount ?? orderTotalForPayment)),
        cash_received: cashReceived,
        pricing_method: 'other',
        fulfillment_type: fulfillmentMode,
        order_type: orderType,
        live_session_id: selectedSessionId,
        commission_staff: commissionStaff.length > 0 ? commissionStaff : undefined,

        // Promotion fields
        loyalty_redeem_points: (values.loyalty_redeem_points as number) || undefined,
        loyalty_redeem_value: (values.loyalty_redeem_value as number) || undefined,
        applied_voucher_codes: (() => {
          const vouchers = values.vouchers as Array<{ code: string }> | undefined;
          return vouchers?.length ? vouchers.map((v) => v.code) : undefined;
        })(),
        voucher_session_id: active.id,
      });

      const orderTotal = orderTotalForPayment;
      setSuccessResult({
        orderCode: created.order_number ?? created.order_id,
        dateISO: new Date().toISOString(),
        total: orderTotal,
        paymentMethod,
        cashReceived,
        change: cashReceived !== undefined ? Math.max(0, cashReceived - orderTotal) : undefined,
        customerName: (values.customer_name as string) || undefined,
        items: orderItems,
      });
      setSuccessOpen(true);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string | string[] } }; message?: string };
      const beMessage = axiosErr?.response?.data?.message;
      const finalMessage = Array.isArray(beMessage)
        ? beMessage.join('; ')
        : beMessage || axiosErr?.message || 'Thanh toán thất bại';
      void message.error(finalMessage);
    } finally {
      setCheckoutLoading(false);
    }
  };

  return (
    <Form
      form={form}
      layout="vertical"
      autoComplete="off"
      onValuesChange={() => bumpFormTick()}
      style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
    >
      {(() => {
        const tabsBar = (
          <PosTabsBar
            tabs={tabsState.tabs}
            activeId={active.id}
            canAddMore={tabsState.tabs.length < MAX_TABS}
            onSwitch={handleSwitchTab}
            onAdd={addTab}
            onCloseRequest={handleCloseTabRequest}
          />
        );
        // Khi App Fullscreen: render tabs vào portal slot trên Header để
        // tiết kiệm chiều cao (40px) cho khu vực order. Khi exit thì render inline.
        return posAppFullscreen && tabsPortalEl ? createPortal(tabsBar, tabsPortalEl) : tabsBar;
      })()}
    <div style={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>
      {/* CENTER: Order panel */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          background: '#fff',
        }}
      >
        {/* Top bar: Search | Nhập DS | Giao Ngay/Đặt Trước toggle */}
        <div
          style={{
            padding: '8px 12px',
            borderBottom: '1px solid #e0e0e0',
            display: 'flex',
            gap: 8,
            alignItems: 'center',
            flexShrink: 0,
          }}
        >
          <div style={{ width: '50%', flexShrink: 0 }}>
            <QRSearchBar fulfillmentMode={fulfillmentMode} onVariantFound={addItem} />
          </div>
          <BulkQrModalButton
            fulfillmentMode={fulfillmentMode}
            onVariantsFound={(variants) => variants.forEach(addItem)}
          />
          <Segmented
            className="segmented-primary"
            value={fulfillmentMode}
            onChange={(v) => handleFulfillmentChange(v as FulfillmentMode)}
            options={[
              { label: 'Giao Ngay', value: 'INSTANT' },
              { label: 'Đặt Trước', value: 'PREORDER' },
            ]}
          />
        </div>

        {showStaleBanner && (
          <GoldPriceStaleBanner
            count={staleCount}
            loading={refreshingPrices}
            onConfirm={() => { void refreshPrices(); }}
            onDismiss={() => setStaleDismissedAt(goldPriceLastUpdated)}
          />
        )}
        <OrderItemsTable
          items={orderItems}
          fulfillmentMode={fulfillmentMode}
          storeId={selectedStoreId}
          onUpdateItem={updateItem}
          onRemoveItem={removeItem}
          onSwapSerial={swapItemSerial}
          swapping={swapping}
          staleItemIds={staleItemIds}
          stalePreviewByItemId={stalePricePreview}
          itemErrors={itemErrors}
        />
      </div>

      {/* RIGHT: Customer info + Payment cluster */}
      <aside
        style={{
          width: 360,
          flexShrink: 0,
          borderLeft: '1px solid #e0e0e0',
          display: 'flex',
          flexDirection: 'column',
          background: '#fafafa',
        }}
      >
        <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <CustomerSidebar
            ref={sidebarRef}
            form={form}
            orderType={orderType}
            onOrderTypeChange={setOrderType}
            selectedStoreId={selectedStoreId}
            selectedSessionId={selectedSessionId}
            onSessionChange={handleSessionChange}
            orderTotal={orderSubtotal}
            hasItems={orderItems.length > 0}
            onClearItems={() => setOrderItems([])}
            fulfillmentMode={fulfillmentMode}
          />
        </div>
        <PaymentFooter
          items={orderItems}
          paymentMethod={paymentMethod}
          onPaymentMethodChange={setPaymentMethod}
          onCheckout={() => void handleCheckout()}
          checkoutDisabled={!isCheckoutReady || checkoutLoading}
          cashReceived={cashReceived}
          onCashReceivedChange={setCashReceived}
          paymentAccountId={paymentAccountId}
          onPaymentAccountChange={setPaymentAccountId}
          nonCashAmount={nonCashAmount}
          onNonCashAmountChange={setNonCashAmount}
        />
      </aside>

      <NfcScanOverlay open={false} error={null} isScanning={false} onClose={() => {}} />

      <CheckoutSuccessDialog
        open={successOpen}
        result={successResult}
        onContinue={() => {
          setSuccessOpen(false);
          closeTab(active.id);
          setSuccessResult(null);
        }}
      />
    </div>
    </Form>
  );
}
